// PURPOSE: Page-scoped outreach settings and booking link editor.
// CONTRACT: AEs read the link; the server authorizes versioned Admin changes.
// DEPENDS: Outreach settings API and shared dialog/menu components.
// CEILING: No provider calls or bookings.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useSession } from './auth';
import { Dialog, ErrorNotice } from './components';
import { Menu } from './outreach-menu';

type Booking = { booking_url: string; version: number };
export function OutreachSettings() {
  const [open, setOpen] = useState(false);
  return <><Menu label="Outreach settings" gear text="Settings"><Link role="menuitem" to="/models/outreach_template">Email templates</Link><button role="menuitem" onClick={() => setOpen(true)}>Booking link</button></Menu>{open && <BookingDialog onClose={() => setOpen(false)} />}</>;
}
export function BookingDialog({ onClose }: { onClose(): void }) {
  const { api, me } = useSession(); const cache = useQueryClient();
  const query = useQuery({ queryKey: ['outreach', 'booking-setting'], queryFn: () => api.request<Booking>('/outreach/settings/booking') });
  const [draft, setDraft] = useState<string>(); const [busy, setBusy] = useState(false); const [error, setError] = useState<unknown>();
  async function save() {
    if (busy || !query.data) return; setBusy(true); setError(undefined);
    try {
      await api.request('/outreach/settings/booking', { method: 'PATCH', body: JSON.stringify({ booking_url: draft ?? query.data.booking_url, version: query.data.version }) });
      await cache.invalidateQueries({ queryKey: ['outreach'] }); onClose();
    } catch (caught) { setError(caught); } finally { setBusy(false); }
  }
  return <Dialog title="Outreach booking link" onClose={() => { if (!busy) onClose(); }}>
    {query.error ? <ErrorNotice error={query.error} retry={() => void query.refetch()} /> : query.isLoading ? <p>Loading…</p> : <form onSubmit={event => { event.preventDefault(); void save(); }}>
      <label className="form-field">Booking link<input type="url" maxLength={2000} readOnly={me.role !== 'Admin'} disabled={busy} value={draft ?? query.data?.booking_url ?? ''} onChange={event => setDraft(event.target.value)} /></label>
      {me.role !== 'Admin' && <p className="muted">An Admin manages this link.</p>}
      {!!error && <ErrorNotice error={error} />}
      <div className="form-footer"><button type="button" className="button secondary" disabled={busy} onClick={onClose}>{me.role === 'Admin' ? 'Cancel' : 'Close'}</button>{me.role === 'Admin' && <button className="button primary" disabled={busy}>Save booking link</button>}</div>
    </form>}
  </Dialog>;
}
